import { SelectOption } from '@shared/components/select';
import { RadioButtonBinaryOptions } from '../enums/radio-buttons-binary-options.enum';
import { isUndefinedOrNull } from '@app/probate/features/home/shared/helpers';

export const getChangedFields = <T extends object>(
  formData: T,
  initialData: T,
): Partial<T> => {
  if (!initialData) return formData;

  const changes: Partial<T> = {};

  const keys = Object.keys(formData) as (keyof T)[];
  for (const key of keys) {
    if (
      Object.prototype.hasOwnProperty.call(formData, key) &&
      formData[key] !== initialData[key as keyof T]
    ) {
      // This is a workaround to avoid type errors when the value is undefined
      // The type of changes is Partial<DecedentRequest> but the value is undefined
      // So we need to cast the value to unknown and then to undefined
      changes[key] = formData[key] as unknown as undefined;
    }
  }

  return changes;
};

export const getOptionsFromEnum = (
  enumObj: object,
  takeLabelAsValue = false,
): SelectOption[] => {
  return Object.entries(enumObj).map(([key, value]) => ({
    label: value,
    value: takeLabelAsValue ? value : key,
  }));
};

export const getValueFromEnumByKey = (enumObj: object, key: string) => {
  return Object.entries(enumObj).find(([enumKey]) => enumKey === key)?.[1];
};

export const getKeyFromEnumByValue = (enumObj: object, value: string) => {
  return Object.entries(enumObj).find(([enumKey]) => enumKey === value)?.[0];
};

export const castToBinaryStringOrNull = (
  value: boolean | null | undefined,
): RadioButtonBinaryOptions | null => {
  if (isUndefinedOrNull(value)) return null;

  return value ? RadioButtonBinaryOptions.Yes : RadioButtonBinaryOptions.No;
};

export const castBinaryStringToBoolean = (
  value: string | null | undefined,
): boolean | null => {
  if (isUndefinedOrNull(value)) return null;

  return value === RadioButtonBinaryOptions.Yes;
};

export const YES_NO_OPTIONS = [
  { id: 'yes', value: 'Yes', label: 'Yes' },
  { id: 'no', value: 'No', label: 'No' },
];

export const toNumber = (v: unknown): number => {
  const n =
    typeof v === 'string' ? Number(v) : (v as number | null | undefined);
  return typeof n === 'number' && !Number.isNaN(n) ? (n as number) : 0;
};
